import React, { useCallback, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { auth } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { downloadCsv } from "../utils/csvExport";
import {
  MAX_UPLOAD_LABEL,
  createPersonalData,
  deletePersonalData,
  listPersonalData,
  triggerFileDownload,
  updatePersonalData,
  validatePersonalFile,
} from "../services/personalDataService";

function formatBytes(n) {
  const num = Number(n) || 0;
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(value) {
  if (!value) return "—";
  try {
    if (typeof value?.toDate === "function") return value.toDate().toLocaleString();
    if (value?.seconds) return new Date(value.seconds * 1000).toLocaleString();
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  } catch {
    /* ignore */
  }
  return String(value);
}

const emptyForm = { title: "", notes: "", file: null };

export default function PersonalDataPage() {
  const user = auth.currentUser;
  const userId = user?.uid || "";
  const email = user?.email || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await listPersonalData(userId);
      setItems(rows);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not load personal data");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFileInputKey((k) => k + 1);
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      try {
        validatePersonalFile(file);
      } catch (err) {
        toast.error(err.message);
        e.target.value = "";
        setForm((f) => ({ ...f, file: null }));
        return;
      }
    }
    setForm((f) => ({ ...f, file }));
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({ title: row.title || "", notes: row.notes || "", file: null });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      toast.error("Please sign in again");
      return;
    }
    setBusy(true);
    if (form.file) setUploadPct(0);
    try {
      if (editingId) {
        const existing = items.find((r) => r.id === editingId) || {};
        await updatePersonalData(editingId, {
          userId,
          title: form.title,
          notes: form.notes,
          file: form.file,
          existing,
          onProgress: setUploadPct,
        });
        toast.success("Personal data updated");
      } else {
        await createPersonalData({
          userId,
          email,
          title: form.title,
          notes: form.notes,
          file: form.file,
          onProgress: setUploadPct,
        });
        toast.success("Personal data saved");
      }
      resetForm();
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Save failed");
    } finally {
      setBusy(false);
      setUploadPct(null);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title || "this item"}"?`)) return;
    setBusy(true);
    try {
      await deletePersonalData(row.id, row, userId);
      toast.success("Deleted");
      if (editingId === row.id) resetForm();
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const onDownloadFile = async (row) => {
    try {
      await triggerFileDownload(row);
    } catch (err) {
      toast.error(err?.message || "Download failed");
    }
  };

  const onDownloadCsv = () => {
    if (!items.length) {
      toast.error("Nothing to download");
      return;
    }
    downloadCsv(
      `personal-data-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "title", header: "Title" },
        { key: "notes", header: "Notes" },
        { key: "fileName", header: "File" },
        { key: "downloadUrl", header: "File URL" },
        { key: "updatedAtText", header: "Updated" },
      ],
      items.map((r) => ({
        title: r.title || "",
        notes: r.notes || "",
        fileName: r.fileName || "",
        downloadUrl: r.downloadUrl || "",
        updatedAtText: formatWhen(r.updatedAt),
      }))
    );
    toast.success("CSV downloaded");
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Personal data</h1>
            <p className="mt-2 text-sm text-slate-600">
              Upload, edit, and download your personal notes and files. Only you can see items saved under your account.
            </p>
            {email ? (
              <p className="mt-1 text-xs text-slate-500">
                Signed in as <span className="font-medium text-slate-700">{email}</span>
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onDownloadCsv}
            disabled={!items.length}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Download all (CSV)
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-slate-800">
            {editingId ? "Edit personal data" : "Add personal data"}
          </h2>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              required
              maxLength={200}
              placeholder="e.g. ID copy, bank detail note"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              placeholder="Optional details…"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              {editingId ? "Replace file (optional)" : "Attach file (optional)"}
            </label>
            <input
              key={fileInputKey}
              type="file"
              onChange={onFileChange}
              disabled={busy}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700"
            />
            <p className="mt-1 text-xs text-slate-400">
              Max {MAX_UPLOAD_LABEL}. Executables are blocked.
              {form.file ? ` Selected: ${form.file.name} (${formatBytes(form.file.size)})` : ""}
            </p>
            {uploadPct != null ? (
              <div className="mt-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-indigo-600 transition-all"
                    style={{ width: `${uploadPct}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">Uploading… {uploadPct}% — keep this tab open.</p>
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={busy || !userId}
              className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? (uploadPct != null ? `Uploading ${uploadPct}%…` : "Saving…") : editingId ? "Save changes" : "Upload & save"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                disabled={busy}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold text-slate-800">Your records</h2>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : !items.length ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
              No personal data yet. Add a title and optional file above.
            </p>
          ) : (
            <ul className="space-y-3">
              {items.map((row) => (
                <li
                  key={row.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-slate-900">{row.title}</h3>
                      {row.notes ? (
                        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{row.notes}</p>
                      ) : null}
                      <p className="mt-2 text-xs text-slate-400">
                        Updated {formatWhen(row.updatedAt)}
                        {row.fileName
                          ? ` · ${row.fileName}${row.size != null ? ` (${formatBytes(row.size)})` : ""}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {row.downloadUrl ? (
                        <button
                          type="button"
                          onClick={() => onDownloadFile(row)}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Download file
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => startEdit(row)}
                        className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(row)}
                        disabled={busy}
                        className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
